import { useCallback, useMemo, useState } from 'react';
import { readReportTargets } from '../../api/custReqShareApi';
import type { WorkOrderNotice } from '../../types/worklog.types';
import type { CustReqApplySubmitItem } from '../../types/custReqShare.types';
import { buildSubmitItems, uncheckedCount } from './custReqApply.logic';
import type { ApplyChecks } from './custReqApply.logic';

/**
 * 반장 작업보고 작성 화면의 고객 요청 반영 상태 — 요청별 [반영했어요] 체크(제출 전 폼 상태).
 * 서버에는 반영 해제가 없고, 체크는 제출 전에만 풀 수 있다.
 */
export function useCustReqApply(notice: WorkOrderNotice | null) {
  // 서버가 작성 권한·공정 확정 여부를 보고 대상을 주므로 비면 영역은 숨는다. 작성 불가(참조인·기한 만료)면 대상을 쓰지 않는다.
  const targets = useMemo(() => (notice?.canWriteReport ? readReportTargets(notice) : []), [notice]);

  const [checks, setChecks] = useState<ApplyChecks>({});

  const setApplied = useCallback((custReqId: number, applied: boolean) => {
    setChecks((prev) => ({ ...prev, [custReqId]: applied }));
  }, []);

  /** 폼 비우기 — 다음 보고용 */
  const reset = useCallback(() => setChecks({}), []);

  /** 제출 호출 직전에 보낼 반영 입력을 고정 */
  const collect = useCallback((): CustReqApplySubmitItem[] => buildSubmitItems(targets, checks), [targets, checks]);

  const unchecked = useMemo(() => uncheckedCount(targets, checks), [targets, checks]);

  return { targets, checks, unchecked, setApplied, reset, collect };
}

export type CustReqApplyCtl = ReturnType<typeof useCustReqApply>;
